// src/lib/authSession.js
//
// Defensive wrapper around supabase.auth.getSession().
//
// Under certain conditions (realtime subscription thrash, flood of failing
// queries, tab-backgrounded token refresh deadlock), supabase.auth.getSession()
// can hang indefinitely — which deadlocks any user action that awaits it
// before firing a fetch. Classic symptom: "Sending…" spinner forever, no
// network request in DevTools.
//
// getSessionWithTimeout resolves to null after N ms if the underlying call
// hasn't settled. Callers decide how to degrade: fire the request without
// auth (server returns 401, user sees actionable toast), or bail out.
//
// Stage 3 (offline mutation queue) will obsolete this — queued actions flush
// when the client recovers. Until then, this prevents infinite spinners.

import { supabase } from './supabase'

export async function getSessionWithTimeout(timeoutMs = 3000) {
  const timeoutPromise = new Promise((resolve) =>
    setTimeout(() => resolve({ data: { session: null }, timedOut: true }), timeoutMs)
  )
  try {
    const result = await Promise.race([supabase.auth.getSession(), timeoutPromise])
    // Normalize: if the supabase call won, shape matches; if timeout won,
    // our sentinel wins. Either way the caller reads result.data.session.
    return result
  } catch (err) {
    console.warn('[authSession] getSession error:', err?.message)
    return { data: { session: null }, error: err }
  }
}

// Convenience — returns just the access token or null, no session object.
export async function getAccessTokenWithTimeout(timeoutMs = 3000) {
  const { data: { session }, timedOut } = await getSessionWithTimeout(timeoutMs)
  if (timedOut) {
    console.warn('[authSession] getSession timed out — falling back to no-auth fetch')
  }
  return session?.access_token || null
}

// Drop-in for supabase.auth.getUser() — SAME return shape ({ data: { user } })
// but timeout-guarded and derived from the session JWT, so it never makes the
// extra validating network round-trip getUser() does (which can hang on a
// half-open socket after sleep and wedge whatever awaits it). For our use —
// reading the user's id/email to scope a query — the JWT user is equivalent.
export async function getUserWithTimeout(timeoutMs = 3000) {
  const { data: { session }, timedOut, error } = await getSessionWithTimeout(timeoutMs)
  return { data: { user: session?.user ?? null }, timedOut, error }
}
